import { Suspense, useState, useCallback, useMemo } from 'react';

import Container from '@/components/Container';
import useIsTablet from '@/hooks/useIsTablet';
import Button from '@/components/Button';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import { useKeyDatesList } from './hooks/useKeyDatesList';
import { useKeyDatesListStore } from './store/useKeyDatesListStore';
import { KeyDateCard } from '../shared/components/key-date-card';
import { KeyDatesListLoading } from './components/loading';
import { useDebounce } from '@/hooks/useDebounce';
import Divider from '@/components/Divider';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import PageHeader from '@/components/PageHeader';

const formatMonthYear = (date: Date) => {
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const KeyDatesListPageContent = () => {
  const { isTablet } = useIsTablet();
  const { loading } = useKeyDatesList();

  const keyDates = useKeyDatesListStore((state) => state.keyDates);
  const currentMonth = useKeyDatesListStore((state) => state.currentMonth);
  const searchTerm = useKeyDatesListStore((state) => state.searchTerm);
  const goToPreviousMonth = useKeyDatesListStore((state) => state.goToPreviousMonth);
  const goToNextMonth = useKeyDatesListStore((state) => state.goToNextMonth);
  const setSearchTerm = useKeyDatesListStore((state) => state.setSearchTerm);
  const page = useKeyDatesListStore((state) => state.page);

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  const { onChangeValue } = useDebounce(setSearchTerm, 300);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearchTerm(value);
      onChangeValue(value);
    },
    [onChangeValue],
  );

  const monthDisplay = useMemo(() => formatMonthYear(currentMonth), [currentMonth]);

  return (
    <>
      <PageHeader title={page?.calendarTitle ?? ''} description={page?.description ?? ''} />
      <Container className="min-h-40" addMargin={!isTablet}>
        <div className="col-span-12">
          <div className=" w-full">
            <input
              type="text"
              placeholder={page?.searchPlaceholder ?? ''}
              value={localSearchTerm}
              onChange={handleSearchChange}
              className="w-full rounded-lg border border-[#D5D7DA] p-4 font-haffer text-sh-xxs outline-none transition-colors placeholder:text-gray-400 focus:border-primary-600"
            />
          </div>

          <Divider className="!border-gray-200" />

          {!localSearchTerm && (
            <div className="mb-sh-30 flex w-full flex-row items-center justify-between gap-4 sm:flex-row sm:items-center sm:justify-between">
              <Button theme="outline" onClick={goToPreviousMonth}>
                <ArrowLeftIcon className="h-5 w-5 !text-dark-orange" />
                <span className="hidden text-sh-xxs font-normal md:block">{page?.previousMonthButton}</span>
              </Button>
              <span className="min-w-[160px] text-center font-haffer text-sh-20-28 font-semibold">{monthDisplay}</span>
              <Button theme="outline" onClick={goToNextMonth}>
                <span className="hidden text-sh-xxs font-normal md:block">{page?.nextMonthButton}</span>
                <ArrowRightIcon className="h-5 w-5 !text-dark-orange" />
              </Button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-sh-60">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 lg:grid-cols-4">
                {keyDates.map((keyDate) => (
                  <AnimatedFadeInComponent key={keyDate.documentId} className="h-full">
                    <KeyDateCard keyDate={keyDate} page={page} />
                  </AnimatedFadeInComponent>
                ))}
              </div>

              {keyDates.length === 0 && (
                <div className="py-sh-60 text-center">
                  <p className="font-haffer text-sh-16-24 text-gray-500">
                    {page?.noKeyDatesFoundText} {localSearchTerm ? `for "${localSearchTerm}"` : monthDisplay}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Container>
    </>
  );
};

export const KeyDatesListPage = () => {
  return (
    <Suspense fallback={<KeyDatesListLoading />}>
      <KeyDatesListPageContent />
    </Suspense>
  );
};
